import { resolveTheme, themeCssVars, type FestivalTheme } from '@mendikot/design-tokens';
import type { ThemeId, ThemeOverrides } from '@mendikot/protocol';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRemoteSettings, useServices, useSettings, useUi } from './context';

const ThemeContext = createContext<FestivalTheme>(resolveTheme('default'));

export const useTheme = () => useContext(ThemeContext);

function applyCssVars(theme: FestivalTheme): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(themeCssVars(theme)))
    root.style.setProperty(name, value);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.palette.bg);
}

/**
 * Festival theming (plan §6): the user's pick wins, otherwise Remote Config decides. A change that
 * arrives mid-hand waits for the next safe point so the table never repaints under a player.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const remote = useRemoteSettings();
  const override = useSettings((s) => s.themeOverride);
  const inHand = useUi((s) => s.inHand);
  const { analytics, crash } = useServices();
  const desired: ThemeId = override ?? remote.themeId;
  // Stable key so a Remote Config re-read with identical values doesn't re-apply the theme.
  const overridesKey = override ? '' : JSON.stringify(remote.themeOverrides);
  const overrides = useMemo(
    () => (overridesKey ? (JSON.parse(overridesKey) as ThemeOverrides) : undefined),
    [overridesKey],
  );
  const [applied, setApplied] = useState(() => resolveTheme(desired, overrides));
  const firstRun = useRef(true);
  const lastLogged = useRef<string | null>(null);

  useEffect(() => {
    if (inHand && !firstRun.current) return;
    const next = resolveTheme(desired, overrides);
    setApplied(next);
    applyCssVars(next);
    crash.setContext({ theme_id: next.id });
    analytics.setUserProperties({ theme_id: next.id });
    if (lastLogged.current !== next.id) {
      lastLogged.current = next.id;
      analytics.log('theme_applied', {
        theme_id: next.id,
        source: override ? 'user' : desired === 'default' ? 'default' : 'remote',
      });
    }
    firstRun.current = false;
  }, [desired, overrides, inHand, override, analytics, crash]);

  const value = useMemo(() => applied, [applied]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
